import React from 'react';

import type * as api from '@gitbook/api-client';
import * as analytics from '@gitbook/app-analytics/client';
import { getChangeRequestBranchId } from '@gitbook/app-hive';
import type { HiveWorkingBranch, HiveWorkingDocument } from '@gitbook/hive-client';
import { useHiveClient } from '@gitbook/hive-react';
import { Icon } from '@gitbook/spine-icons';
import { type OverlayTriggerState, toast } from '@gitbook/spine-react';
import {
    type LoadingStateHookResult,
    useEventCallback,
    useLoadingState,
} from '@gitbook/util-react-hooks';
import { assertIsDefined } from '@gitbook/util-ts';

import { useNavigation } from '../../navigation';
import { apiClient } from '../../state';

const ChangeRequestMergeContext = React.createContext<{
    mergeChangeRequest: () => Promise<void>;
    updateChangeRequest: () => Promise<void>;
    onMerge: (dialogTrigger: OverlayTriggerState) => Promise<void>;
    mergeLoadable: LoadingStateHookResult;
    updateLoadable: LoadingStateHookResult;
    isOutOfDate: boolean;
} | null>(null);

type ChangeRequestMergeProviderValue = {
    space: api.Space;
    changeRequest: api.ChangeRequest | null;
    analyticsContext: analytics.AnalyticsContextSpaceContent;
};

/**
 * Provider for the change request merge actions.
 */
export function ChangeRequestMergeProvider(props: {
    value: ChangeRequestMergeProviderValue;
    children: React.ReactNode;
}) {
    const { value, children } = props;
    const { space, changeRequest, analyticsContext } = value;

    const { navigate } = useNavigation();
    const hiveClient = useHiveClient();

    const updateLoadable = useLoadingState();
    const mergeLoadable = useLoadingState();
    const [, wrapLoadingUpdate] = updateLoadable;
    const [loadingMerge, wrapLoadingMerge] = mergeLoadable;

    const mergeChangeRequest = async () => {
        await wrapLoadingMerge(async () => {
            await toast.catch(async () => {
                assertIsDefined(changeRequest, 'changeRequest is required to merge');
                await hiveClient.sync.withReadOnlyDataSyncs(async () => {
                    const { data } = await apiClient.changeRequests.mergeChangeRequest(
                        space.id,
                        changeRequest.id
                    );

                    if (data.result === 'merge') {
                        toast('Change request merged', {
                            description: `${
                                space.title || 'The space'
                            } has been updated with the changes.`,
                            icon: Icon.ChangeRequestMerged,
                        });

                        navigate({
                            screen: {
                                space: space.id,
                                changeRequest: undefined,
                                revision: undefined,
                                mode: undefined,
                                sideSheet: undefined,
                            },
                        });
                    }
                }, checkIsAffectedSyncedFactory({ space, changeRequest }));
            }, 'Error while merging change request');
        });
    };

    /** User updated the CR with changes on primary content */
    const updateChangeRequest = useEventCallback(async () => {
        assertIsDefined(changeRequest, 'changeRequest is required to update');
        await wrapLoadingUpdate(async () => {
            await hiveClient.sync.withReadOnlyDataSyncs(async () => {
                await toast.catch(async () => {
                    const {
                        data: { result },
                    } = await apiClient.changeRequests.updateChangeRequest(
                        space.id,
                        changeRequest.id
                    );

                    if (result !== 'conflicts') {
                        toast.success('Change request updated', {
                            description:
                                'Changes made recently on the space have been updated in the change request.',
                        });
                    }
                }, 'Error while updating change request');
            }, checkIsAffectedSyncedFactory({ space, changeRequest }));
        });

        analytics.logEvent({
            ...analyticsContext,
            type: 'change_request_update',
            changeRequest: changeRequest.id,
        });
    });

    // When merging, the main branch will be updated before the change-request is updated.
    // we wait for the merge to finish before deciding if we should consider the CR as out of date.
    const isOutOfDateBeforeMergingRef = React.useRef(changeRequest?.outdated ?? false);
    React.useLayoutEffect(() => {
        if (!loadingMerge) {
            isOutOfDateBeforeMergingRef.current = changeRequest?.outdated ?? false;
        }
    });
    const isOutOfDate = loadingMerge
        ? isOutOfDateBeforeMergingRef.current
        : (changeRequest?.outdated ?? false);

    const onMerge = useEventCallback(async (dialogTrigger: OverlayTriggerState) => {
        // Blur the document to flush any title/description changes.
        blurDocument();

        if (isOutOfDate) {
            dialogTrigger.open();
        } else {
            await mergeChangeRequest();
        }
    });

    return (
        <ChangeRequestMergeContext.Provider
            value={{
                mergeChangeRequest,
                updateChangeRequest,
                onMerge,
                mergeLoadable,
                updateLoadable,
                isOutOfDate,
            }}
        >
            {children}
        </ChangeRequestMergeContext.Provider>
    );
}

function blurDocument() {
    if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
    }
}

/**
 * Hook to use the change request merge actions.
 * Must be used within a ChangeRequestMergeProvider.
 */
export function useChangeRequestMergeActions() {
    const context = React.useContext(ChangeRequestMergeContext);
    if (!context) {
        throw new Error(
            'useChangeRequestMergeActions must be used within a ChangeRequestMergeProvider'
        );
    }
    return context;
}

function checkIsAffectedSyncedFactory(input: {
    space: api.Space;
    changeRequest: api.ChangeRequest;
}) {
    const { space, changeRequest } = input;
    return (sync: HiveWorkingBranch | HiveWorkingDocument) => {
        return (
            sync.contentId === space.id &&
            sync.branchId === getChangeRequestBranchId(changeRequest.id)
        );
    };
}
